/**
 * The design tokens. The ONLY source of type sizes, colours, spacing and motion.
 *
 * Every other file refers to these by name - `C.textDim`, `fs(14)` - or, in CSS
 * and class strings, by the custom properties `applyTokens` writes onto :root
 * before the first render (`var(--ink-text-dim)`, `var(--fs-14)`). Data colours
 * (the depth, velocity and hazard ramps) are not here: they live in
 * backend/render/colormaps.py and arrive from GET /api/colormaps.
 *
 * backend/tests/test_design_tokens.py fails the build on any arbitrary text size,
 * numeric inline font size or hex colour written anywhere else in frontend/src.
 */

/** The type scale. Any other size is a bug. */
export const FONT_SIZES = [11, 12, 14, 16, 20, 24, 32, 48] as const
export type FontSize = (typeof FONT_SIZES)[number]

/** A permitted size: the requested one, or the nearest permitted size to it. */
export function fs(size: number): FontSize {
  return FONT_SIZES.reduce((best, s) => (Math.abs(s - size) < Math.abs(best - size) ? s : best), FONT_SIZES[0])
}

export const lineHeight = (size: number) => (size >= 24 ? 1.2 : 1.45)
export const letterSpacing = (size: number) => (size >= 32 ? '-0.02em' : '0')

/** Semantic type roles. */
export const TYPE = {
  label: 11, // uppercase, 0.06em
  small: 12,
  body: 14,
  cell: 14,
  subheading: 16,
  heading: 20,
  display: 24,
  title: 32,
  figure: 48,
} as const

export const SPACING = [4, 8, 12, 16, 24, 32, 48, 64] as const

export const MOTION = {
  hoverMs: 120,
  panelMs: 240,
  ease: 'cubic-bezier(0.22, 1, 0.36, 1)',
} as const

/** Colours. One ink ground, one raised surface, one hairline, one accent. */
export const C = {
  bg: '#0A0C10',
  surface: '#111419',
  surfaceRaised: '#171B21',
  border: 'rgba(255, 255, 255, 0.08)',
  borderStrong: 'rgba(255, 255, 255, 0.14)',
  grid: 'rgba(255, 255, 255, 0.06)',
  text: '#E6EAF0',
  textDim: '#9AA4B2',
  textFaint: '#6B7480',
  /** The one accent: the active state, and nothing else. Green, so nothing over a map reads as water. */
  accent: '#8FD694',
  accentDim: '#548F59',
  warn: '#E39A2E',
  alert: '#BE402A',
  alertText: '#EAB0A6',
  alertDim: '#5C1F14',
  ok: '#5AA469',
  // CAP severity, one colour each wherever severity appears.
  sevExtreme: '#D9534F',
  sevSevere: '#E8913A',
  sevModerate: '#D9C24F',
  sevMinor: '#B8C46A',
  // Site hazard markers.
  hazardDam: '#F2C14E',
  hazardFlash: '#E0712F',
  // One colour per model on the Comparison page, in this fixed order (Delft3D, DualSPHysics, QuickFlood).
  // Checked with the dataviz palette validator against the dark surface: lightness band, chroma,
  // CVD and normal-vision separation, contrast all pass. No blue.
  modelReference: '#E6EAF0',
  modelDelft3d: '#B8862C',
  modelSph: '#B45CA8',
  modelQuickflood: '#6AA64A',
  // Extent agreement, cell by cell: both wet (a neutral grey-green), the model wet where the reference is
  // dry (amber), the reference wet where the model is dry (deep red). Amber and red separate for CVD.
  extentAgree: '#AEB8A6',
  extentOver: '#C47A1C',
  extentMissed: '#B03A2C',
  // The 3-D view's sky: a dust-haze gradient, never blue; distant ground fades to the horizon colour.
  skyTop: '#16181D',
  skyHorizon: '#6E665C',
  /** The one chrome cluster over a map canvas (MapCorner): translucent ink. */
  chrome: 'rgba(10, 12, 16, 0.72)',
  // Vector lines drawn over imagery.
  mapRiver: '#F4D35E',
  mapBoundary: '#D6CFC4',
  mapDistrict: '#A89F92',
  mapInfraOut: '#C8C2B8',
  mapExtent: '#E6EAF0',
  /** A dry settlement marker's fill. */
  mapMarkerFill: '#111419',
} as const

/** The Tailwind palettes the class names use, re-pointed at the tokens above. */
export const PALETTE: Record<string, Record<string, string>> = {
  slate: {
    50: '#F1F4F7', 100: '#E6EAF0', 200: '#DCE1E8', 300: '#C3CAD4', 400: '#9AA4B2', 500: '#7C8592',
    600: '#6B7480', 700: '#2E333B', 800: '#22262D', 900: '#111419', 950: '#0A0C10',
  },
  sky: {
    50: '#F1FAF2', 100: '#DFF3E1', 200: '#C3E8C7', 300: '#A9DEAE', 400: '#9BD9A0', 500: '#8FD694',
    600: '#6FB875', 700: '#548F59', 800: '#3D6A41', 900: '#27452A', 950: '#1A2E1C',
  },
  amber: {
    50: '#FCF4E6', 100: '#F8E6C6', 200: '#F3CF94', 300: '#EEB65F', 400: '#E39A2E', 500: '#D08A22',
    600: '#B0741C', 700: '#8A5B16', 800: '#5F3F10', 900: '#3D290A', 950: '#2A1C07',
  },
  rose: {
    50: '#FAECEA', 100: '#F3D2CD', 200: '#EAB0A6', 300: '#E08A79', 400: '#D0614B', 500: '#BE402A',
    600: '#A13523', 700: '#7F2A1C', 800: '#5C1F14', 900: '#3C150E', 950: '#2A0F0A',
  },
  emerald: {
    50: '#EDF6EF', 100: '#D5EADB', 200: '#B3D9BA', 300: '#8FC79A', 400: '#6FB57C', 500: '#5AA469',
    600: '#488A55', 700: '#386B42', 800: '#284D30', 900: '#1A3320', 950: '#122316',
  },
}

function channels(hex: string): string {
  const h = hex.replace('#', '')
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)).join(' ')
}

/** kebab-case custom property name for a colour token: textDim -> --ink-text-dim. */
export const cssVar = (name: keyof typeof C) => `--ink-${name.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}`

/** Write every token onto :root as a custom property. Called once, before render. */
export function applyTokens(root: HTMLElement = document.documentElement): void {
  const set = (k: string, v: string) => root.style.setProperty(k, v)
  for (const [name, value] of Object.entries(C)) set(cssVar(name as keyof typeof C), value)
  set('--ink-accent-rgb', channels(C.accent))
  for (const [palette, steps] of Object.entries(PALETTE)) {
    for (const [step, value] of Object.entries(steps)) set(`--tw-${palette}-${step}`, channels(value))
  }
  for (const size of FONT_SIZES) set(`--fs-${size}`, `${size}px`)
  // --fs-label is the small-text role; only the uppercase label classes take 11 px.
  set('--fs-label', `${TYPE.small}px`)
  set('--fs-small', `${TYPE.small}px`)
  set('--fs-cell', `${TYPE.cell}px`)
  set('--fs-body', `${TYPE.body}px`)
  set('--fs-heading', `${TYPE.heading}px`)
  set('--fs-title', `${TYPE.title}px`)
  set('--fs-figure', `${TYPE.figure}px`)
  SPACING.forEach((v, i) => set(`--s${i + 1}`, `${v}px`))
  set('--r-control', '4px')
  set('--r-panel', '6px')
  set('--row-h', '36px')
  set('--t-fast', `${MOTION.hoverMs}ms`)
  set('--t-base', `${MOTION.panelMs}ms`)
  set('--t-slow', `${MOTION.panelMs}ms`)
  set('--ease', MOTION.ease)
}
